import { Controller } from "stimulus";
import { stateParams, fetchParams, applyFilter, MOBILE_QUERY } from "../../training_grooming_calendar/url_state";
import { nowOffset } from "../../training_grooming_calendar/now_line";
import { closeOverlay } from "../../training_grooming_calendar/overlay";
import { captureScroll, restoreScroll } from "../../training_grooming_calendar/scroll_state";
import { FILTER_RELOAD } from "../../training_grooming_calendar/filter_sections";
import { createFilterQueue } from "../../training_grooming_calendar/filter_queue";
import { createLatest } from "../../training_grooming_calendar/fill_rate_compare";

export default class extends Controller {
  static values = {
    url: String,
    fillRateUrl: String,
    date: String,
    view: String,
    tab: String,
    facility: String,
    threshold: String,
    scope: String,
    categories: Array,
    hiddenStatuses: Array,
    lanes: String,
    hiddenServices: Array,
    blocks: String,
    schedules: String,
    scheduled: String,
    empty: String,
  };

  static targets = ["content", "scroller", "nowLine"];

  connect() {
    this.latest = createLatest();
    this.collapsed = new Set();
    this.limits = {};
    this.filterQueue = createFilterQueue({ send: () => this.load({ reason: FILTER_RELOAD }) });
    this.media = window.matchMedia(MOBILE_QUERY);
    this.handleMedia = () => this.load();
    this.media.addEventListener("change", this.handleMedia);
    this.load();
  }

  disconnect() {
    this.media.removeEventListener("change", this.handleMedia);
    this.filterQueue.cancel();
    this.stopClock();
  }

  goTo(event) {
    this.announce(event.currentTarget);
    this.dateValue = event.currentTarget.dataset.date;
    this.load();
  }

  setView(event) {
    const button = event.currentTarget;
    this.announce(button);
    this.element.querySelectorAll(".tg-view").forEach((view) => {
      const active = view.dataset.view === button.dataset.view;
      view.classList.toggle("active", active);
      view.setAttribute("aria-pressed", active ? "true" : "false");
    });
    this.viewValue = button.dataset.view;
    this.load();
  }

  setThreshold(event) {
    const button = event.currentTarget;
    this.element.querySelectorAll("[data-threshold]").forEach((option) => {
      const active = option.dataset.threshold === button.dataset.threshold;
      option.classList.toggle("active", active);
      option.setAttribute("aria-pressed", active ? "true" : "false");
    });
    this.thresholdValue = button.dataset.threshold;
    this.load();
  }

  pickThreshold(event) {
    this.thresholdValue = event.currentTarget.value;
    this.load();
  }

  setScope(event) {
    const button = event.currentTarget;
    this.element.querySelectorAll("[data-scope]").forEach((option) => {
      const active = option.dataset.scope === button.dataset.scope;
      option.classList.toggle("active", active);
      option.setAttribute("aria-pressed", active ? "true" : "false");
    });
    this.element.querySelectorAll(".tg-fr-period-sub").forEach((node) => (node.textContent = ""));
    this.scopeValue = button.dataset.scope;
    this.load();
  }

  announce(control) {
    const { label, periodName, periodSub } = control.dataset;
    if (label) this.element.querySelectorAll(".tg-stepper-label").forEach((node) => (node.textContent = label));
    if (periodName) this.element.querySelectorAll("[data-fr-period-name]").forEach((node) => (node.textContent = periodName));
    if (periodSub !== undefined) this.element.querySelectorAll("[data-fr-period-sub]").forEach((node) => (node.textContent = periodSub));
  }

  setTab(event) {
    const tab = event.currentTarget.dataset.tab;
    this.element.querySelectorAll(".tg-tab").forEach((button) => {
      const active = button.dataset.tab === tab;
      button.classList.toggle("active", active);
      button.setAttribute("aria-selected", active ? "true" : "false");
    });
    this.tabValue = tab;
    this.load();
  }

  openDay(event) {
    this.viewValue = "day";
    this.dateValue = event.currentTarget.dataset.date;
    this.load();
  }

  filter(event) {
    const { state, limits } = applyFilter(this.state(), this.limits, event.detail);
    this.limits = limits;
    this.facilityValue = state.facility;
    this.categoriesValue = state.categories;
    this.hiddenStatusesValue = state.hiddenStatuses;
    this.lanesValue = state.lanes;
    this.hiddenServicesValue = state.hiddenServices;
    this.blocksValue = state.blocks;
    this.schedulesValue = state.schedules;
    this.scheduledValue = state.scheduled;
    this.emptyValue = state.empty;
    this.latest.next();
    this.contentTarget.classList.add("tg-content-loading");
    this.writeUrl();
    if (event.detail.reset) {
      this.filterQueue.flush();
    } else {
      this.filterQueue.change();
    }
  }

  showAllFacilities() {
    this.facilityValue = "all";
    this.limits = {};
    this.load();
  }

  loadMore(event) {
    const { kind, limit } = event.currentTarget.dataset;
    const group = event.currentTarget.closest(".tg-group");
    this.limits[kind] = Number(limit);
    this.focusLane = group && { kind, index: group.querySelectorAll(".tg-lane-head").length };
    this.load();
  }

  reload(event) {
    this.load({ keepScroll: true, reason: event && event.detail ? event.detail.reason : undefined });
  }

  pickDate(event) {
    const instance = flatpickr(event.currentTarget, {
      disableMobile: "true",
      defaultDate: this.dateValue,
      onChange: (selectedDates, dateStr) => {
        if (selectedDates.length !== 1) return;
        instance.destroy();
        this.dateValue = dateStr;
        this.load();
      },
    });
    instance.open();
  }

  toggleGroup(event) {
    const kind = event.currentTarget.closest(".tg-group").dataset.kind;
    this.collapsed.has(kind) ? this.collapsed.delete(kind) : this.collapsed.add(kind);
    this.applyCollapsed();
  }

  load({ keepScroll = false, reason = "other" } = {}) {
    this.filterQueue.cancel();
    const token = this.latest.next();
    const scroll = keepScroll || this.tabValue === "fill_rate" ? captureScroll(this.contentTarget, window) : null;
    delete this.contentTarget.dataset.tgRestoring;
    const params = fetchParams(this.state(), { mobile: this.media.matches, limits: this.limits });
    this.contentTarget.classList.add("tg-content-loading");
    this.contentTarget.setAttribute("aria-busy", "true");
    const url = this.tabValue === "fill_rate" ? this.fillRateUrlValue : this.urlValue;
    fetch(`${url}?${params.toString()}`, { headers: { "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        if (!this.latest.isCurrent(token)) return;
        closeOverlay({ restoreFocus: false });
        this.contentTarget.dataset.tgLoadReason = reason;
        this.contentTarget.innerHTML = html;
        this.contentTarget.classList.remove("tg-content-loading");
        this.contentTarget.removeAttribute("aria-busy");
        this.afterRender(scroll);
      })
      .catch(() => {
        if (!this.latest.isCurrent(token)) return;
        this.contentTarget.classList.remove("tg-content-loading");
        this.contentTarget.removeAttribute("aria-busy");
        this.contentTarget.innerHTML = '<div class="tg-error" role="alert">The calendar could not be loaded. Please refresh the page and try again.</div>';
      });
  }

  writeUrl() {
    const url = `${window.location.pathname}?${stateParams(this.state()).toString()}`;
    window.history.replaceState(window.history.state, "", url);
  }

  afterRender(scroll) {
    this.writeUrl();
    this.applyCollapsed();
    if (scroll) {
      this.contentTarget.dataset.tgRestoring = "true";
      restoreScroll(this.contentTarget, window, scroll);
    } else {
      this.scrollToOffset();
    }
    this.startClock();
    this.focusNewLane();
    this.element.dispatchEvent(new CustomEvent("training-grooming:rendered"));
  }

  focusNewLane() {
    if (!this.focusLane) return;
    const { kind, index } = this.focusLane;
    this.focusLane = null;
    const head = this.contentTarget.querySelectorAll(`.tg-group[data-kind="${kind}"] .tg-lane-head`)[index];
    if (head) head.focus();
  }

  state() {
    return {
      date: this.dateValue,
      view: this.viewValue,
      tab: this.tabValue,
      facility: this.facilityValue,
      threshold: this.thresholdValue,
      scope: this.scopeValue,
      categories: this.categoriesValue,
      hiddenStatuses: this.hiddenStatusesValue,
      lanes: this.lanesValue,
      hiddenServices: this.hiddenServicesValue,
      blocks: this.blocksValue,
      schedules: this.schedulesValue,
      scheduled: this.scheduledValue,
      empty: this.emptyValue,
    };
  }

  applyCollapsed() {
    this.contentTarget.querySelectorAll(".tg-group").forEach((group) => {
      const collapsed = this.collapsed.has(group.dataset.kind);
      group.classList.toggle("tg-group-collapsed", collapsed);
      const button = group.querySelector("[data-action~='training-grooming-calendar--calendar#toggleGroup']");
      if (button) button.setAttribute("aria-expanded", collapsed ? "false" : "true");
    });
  }

  scrollToOffset() {
    if (!this.hasScrollerTarget) return;
    this.scrollerTarget.scrollTop = Number(this.scrollerTarget.dataset.scrollOffset || 0);
  }

  startClock() {
    this.stopClock();
    if (!this.hasNowLineTarget) return;
    const line = this.nowLineTarget;
    const renderedOffset = Number(line.dataset.offset);
    const renderedAt = Date.now();
    const windowLength = Number(getComputedStyle(line).getPropertyValue("--tg-window")) || Infinity;
    this.clock = setInterval(() => {
      const offset = nowOffset({ renderedOffset, renderedAt, now: Date.now(), windowLength });
      line.hidden = offset === null;
      if (offset !== null) line.style.setProperty("--tg-now", offset);
    }, 30 * 1000);
  }

  stopClock() {
    if (this.clock) clearInterval(this.clock);
    this.clock = null;
  }
}
